import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  TrendingUp, Star, Award, AlertTriangle, Users, RefreshCw,
  ChevronRight, BarChart3, Target
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const KPI_COLORS = {
  excellent: { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: '#10B981' },
  good: { bg: 'bg-blue-100', text: 'text-blue-700', bar: '#3B82F6' },
  average: { bg: 'bg-amber-100', text: 'text-amber-700', bar: '#F59E0B' },
  poor: { bg: 'bg-red-100', text: 'text-red-700', bar: '#EF4444' },
};

function getKpiTier(score) {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'average';
  return 'poor';
}

export default function KPIDashboardPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [file201, setFile201] = useState(null);

  const fetchKPIs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/escalation/kpi`);
      setEmployees(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('KPI load error:', e?.response?.status, e?.message);
      setEmployees([]);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchKPIs(); }, [fetchKPIs]);

  const recalculate = async (empId) => {
    try {
      await axios.post(`${API}/api/escalation/kpi/recalculate/${empId}`);
      toast.success('KPI recalculated');
      fetchKPIs();
    } catch { toast.error('Recalculation failed'); }
  };

  const view201 = async (empId) => {
    try {
      const { data } = await axios.get(`${API}/api/escalation/201/${empId}`);
      setFile201(data);
      setSelected(empId);
    } catch { toast.error('Failed to load 201 file'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>;

  const avgKpi = employees.length > 0 ? Math.round(employees.reduce((s, e) => s + (e.kpi_score || 0), 0) / employees.length) : 0;
  const chartData = employees.filter(e => e.kpi_score > 0).map(e => ({
    name: `${e.first_name || ''} ${(e.last_name || '').charAt(0)}.`.trim(),
    score: e.kpi_score || 0,
    rating: (e.avg_customer_rating || 0),
  }));

  const radarData = selected && file201?.stats ? [
    { metric: 'Rating', value: Math.round(((file201.stats.client_feedbacks || 0) > 0 ? 1 : 0) * 40), max: 40 },
    { metric: 'Completion', value: Math.round((employees.find(e => e.id === selected)?.kpi_breakdown?.completion_score || 0)), max: 30 },
    { metric: 'Attitude', value: Math.round((employees.find(e => e.id === selected)?.kpi_breakdown?.attitude_score || 0)), max: 30 },
    { metric: 'Reviews', value: Math.min(20, (employees.find(e => e.id === selected)?.total_reviews || 0) * 4), max: 20 },
    { metric: 'Jobs Done', value: Math.min(20, (employees.find(e => e.id === selected)?.total_jobs_completed || 0) * 2), max: 20 },
  ] : [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="kpi-dashboard-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-emerald-500" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Performance Dashboard<PageInfoTooltip pageKey="kpi-dashboard" /></h1>
            <p className="text-xs text-gray-400">{employees.length} employees tracked &middot; Avg KPI: {avgKpi}/100</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={fetchKPIs}><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Avg KPI Score', value: avgKpi, icon: Target, color: 'bg-emerald-50 text-emerald-600' },
          { label: 'Excellent (80+)', value: employees.filter(e => (e.kpi_score || 0) >= 80).length, icon: Star, color: 'bg-blue-50 text-blue-600' },
          { label: 'Needs Improvement (<40)', value: employees.filter(e => (e.kpi_score || 0) > 0 && (e.kpi_score || 0) < 40).length, icon: AlertTriangle, color: 'bg-red-50 text-red-600' },
          { label: 'Not Scored', value: employees.filter(e => !(e.kpi_score || 0)).length, icon: Users, color: 'bg-gray-50 text-gray-600' },
        ].map(item => (
          <Card key={item.label} className="bg-white border-gray-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", item.color.split(' ')[0])}>
                  <item.icon className={cn("h-4 w-4", item.color.split(' ')[1])} />
                </div>
                <span className="text-xs text-gray-500">{item.label}</span>
              </div>
              <span className="text-2xl font-bold text-text-primary">{item.value}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Leaderboard */}
        <Card className="bg-white border-gray-border lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" />Leaderboard</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {employees.map((emp, i) => {
                const tier = getKpiTier(emp.kpi_score || 0);
                const colors = KPI_COLORS[tier];
                return (
                  <div key={emp.id} className={cn("flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer",
                    selected === emp.id ? "bg-emerald-50 border-emerald-200" : "border-gray-100 hover:border-gray-200"
                  )} onClick={() => view201(emp.id)} data-testid={`kpi-emp-${emp.id}`}>
                    <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold",
                      i === 0 && (emp.kpi_score || 0) > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"
                    )}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{emp.first_name} {emp.last_name}</p>
                      <p className="text-[10px] text-gray-400">{emp.position || emp.department}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge className={cn("text-[9px]", colors.bg, colors.text)}>{emp.kpi_score || 0}</Badge>
                      {emp.avg_customer_rating > 0 && (
                        <p className="text-[9px] text-amber-500 flex items-center gap-0.5 mt-0.5 justify-end"><Star className="h-2.5 w-2.5" />{emp.avg_customer_rating}</p>
                      )}
                    </div>
                  </div>
                );
              })}
              {employees.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No employees found</p>}
            </div>
          </CardContent>
        </Card>

        {/* Charts */}
        <Card className="bg-white border-gray-border lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-500" />KPI Scores</CardTitle></CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `${v}/100`} />
                  <Bar dataKey="score" name="KPI Score" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={KPI_COLORS[getKpiTier(entry.score)].bar} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-12 text-center"><p className="text-sm text-gray-400">No KPI data yet. Submit client feedback to generate scores.</p></div>
            )}

            {/* Selected Employee Breakdown */}
            {selected && file201 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm">{file201.employee.name} — KPI Breakdown</h4>
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => recalculate(selected)}>
                    <RefreshCw className="h-3 w-3 mr-1" />Recalculate
                  </Button>
                </div>
                {(() => {
                  const emp = employees.find(e => e.id === selected);
                  const bd = emp?.kpi_breakdown || {};
                  return (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-white rounded-lg border">
                        <Star className="h-5 w-5 mx-auto text-amber-400 mb-1" />
                        <p className="text-lg font-bold">{bd.rating_score || 0}<span className="text-xs text-gray-400">/40</span></p>
                        <p className="text-[10px] text-gray-500">Client Rating</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border">
                        <Target className="h-5 w-5 mx-auto text-blue-400 mb-1" />
                        <p className="text-lg font-bold">{bd.completion_score || 0}<span className="text-xs text-gray-400">/30</span></p>
                        <p className="text-[10px] text-gray-500">Job Completion</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border">
                        <Award className="h-5 w-5 mx-auto text-emerald-400 mb-1" />
                        <p className="text-lg font-bold">{bd.attitude_score || 0}<span className="text-xs text-gray-400">/30</span></p>
                        <p className="text-[10px] text-gray-500">Attitude ({bd.warnings || 0} warn, {bd.commendations || 0} comm)</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
